import { queryTokens, normalizeName, matchesAllPrefixes, buildSnippet, appendHighlighted, formatSearchDate } from "./search_text";

const DEBOUNCE_MS = 300;
// Shorter words are left to the chat names: a one-letter message search only
// matches the word itself and would cost a request per keystroke.
const MIN_MESSAGE_TOKEN_LENGTH = 2;
const PAGE_SIZE = 20;
const MEDIA_PREFIXES = { "m.image": "📷 ", "m.video": "🎬 ", "m.audio": "🎵 ", "m.file": "📎 " };

// The search field above the room list, the way WhatsApp's works: typing
// narrows the chats to those whose name matches — at once, on the client —
// and lists below them the messages across all chats that contain the words,
// from the search engine and debounced. A message opens its chat at that
// message. The state lives here and the DOM only shows it, so the room list
// re-rendering on every sync event does not disturb it.
export class RoomListSearch {
  constructor(ui, root) {
    this.ui = ui;
    this.input = root.querySelector(".bryght-room-search__input");
    this.clearButton = root.querySelector(".bryght-room-search__clear");
    this.scroller = root.querySelector(".bryght-rooms-scroller");
    this.chatsTitle = root.querySelector(".bryght-search-section-title--chats");
    this.results = root.querySelector(".bryght-message-results");
    this.resultsTitle = this.results.querySelector(".bryght-message-results__title");
    this.list = this.results.querySelector(".bryght-message-results__list");
    this.status = this.results.querySelector(".bryght-message-results__status");
    this.hint = this.results.querySelector(".bryght-message-results__hint");
    this.moreButton = this.results.querySelector(".bryght-message-results__more");

    this.query = "";
    this.tokens = [];
    this.nameTokens = [];
    this.normalizedNames = new Map();
    this.visibleChats = 0;
    this.rows = [];
    this.cursor = null;
    this.state = "idle"; // idle, loading, done or error
    this.requestId = 0;
    this.controller = null;
    this.debounceTimer = null;

    // A chat restored from Turbo's snapshot cache comes back with the old
    // text in the field but none of the state behind it.
    this.input.value = "";
    this.clearButton.hidden = true;

    this.input.addEventListener("input", () => this.setQuery(this.input.value));
    this.input.addEventListener("keydown", (e) => this.handleKeydown(e));
    this.clearButton.addEventListener("click", () => {
      this.clear();
      this.input.focus();
    });
    this.moreButton.addEventListener("click", () => this.searchMessages({ append: true }));
    this.list.addEventListener("click", (e) => {
      const button = e.target.closest(".bryght-message-hit");
      const row = button && this.rows.find((candidate) => candidate.button === button);
      if (row) this.openHit(row.hit);
    });
  }

  destroy() {
    clearTimeout(this.debounceTimer);
    this.controller?.abort();
  }

  isActive() {
    return this.query !== "";
  }

  searchesMessages() {
    return this.tokens.some((token) => token.length >= MIN_MESSAGE_TOKEN_LENGTH);
  }

  handleKeydown(e) {
    if (e.key === "Escape") {
      e.preventDefault();
      if (this.input.value) this.clear();
      else this.input.blur();
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (this.searchesMessages()) this.searchMessages();
    }
  }

  clear() {
    this.input.value = "";
    this.setQuery("");
  }

  setQuery(value) {
    this.clearButton.hidden = value === "";
    const query = value.trim();
    if (query === this.query) return;

    this.query = query;
    this.tokens = queryTokens(query);
    this.nameTokens = queryTokens(normalizeName(query));
    this.resetMessages();
    if (this.scroller) this.scroller.scrollTop = 0;

    if (this.searchesMessages()) {
      this.state = "loading";
      this.debounceTimer = setTimeout(() => this.searchMessages(), DEBOUNCE_MS);
    }

    this.ui.renderRoomList();
    this.render();
  }

  // Chat names match at word starts, accents aside: "mul" finds "Müller".
  matchesRoom(displayInfo) {
    if (!this.isActive()) return true;
    if (this.nameTokens.length === 0) return false;

    const name = displayInfo?.name || "";
    let normalized = this.normalizedNames.get(name);
    if (normalized === undefined) {
      normalized = normalizeName(name);
      this.normalizedNames.set(name, normalized);
    }
    return matchesAllPrefixes(normalized, this.nameTokens);
  }

  // Called by renderRoomList with the number of chats it let through. Names
  // Rails answers late arrive the same way, so the hit rows pick them up here.
  onRoomListRendered(visibleCount) {
    this.visibleChats = visibleCount;
    this.rows.forEach((row) => this.fillNames(row));
    this.render();
  }

  resetMessages() {
    clearTimeout(this.debounceTimer);
    this.controller?.abort();
    this.controller = null;
    this.requestId += 1;
    this.rows = [];
    this.list.replaceChildren();
    this.cursor = null;
    this.state = "idle";
  }

  async searchMessages({ append = false } = {}) {
    clearTimeout(this.debounceTimer);
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const requestId = ++this.requestId;
    const tokens = this.tokens;

    this.state = "loading";
    this.render();

    try {
      const { hits, cursor } = await this.ui.searchEngine.searchMessages({
        term: this.query,
        tokens,
        cursor: append ? this.cursor : null,
        limit: PAGE_SIZE,
        signal: controller.signal
      });
      // A newer query (or a reset) took over while this one was in flight.
      if (requestId !== this.requestId) return;

      if (!append) {
        this.rows = [];
        this.list.replaceChildren();
      }
      const shown = new Set(this.rows.map(({ hit }) => hit.eventId));
      hits.filter((hit) => !shown.has(hit.eventId)).forEach((hit) => this.addRow(hit, tokens));
      this.cursor = cursor;
      this.state = "done";
    } catch (error) {
      if (requestId !== this.requestId) return;
      console.error("[BryghtChat] Message search failed:", error);
      this.state = "error";
    }

    this.render();
  }

  // Built from text nodes only: the message text is the sender's, not ours.
  addRow(hit, tokens) {
    const room = this.ui.client.getRoom(hit.roomId);
    if (!room) return;
    const info = this.ui.getRoomDisplayInfo(room);

    const button = document.createElement("button");
    button.type = "button";
    button.className = "bryght-message-hit";

    const body = element("span", "bryght-message-hit__body");
    const top = element("span", "bryght-message-hit__top");
    const roomName = element("span", "bryght-message-hit__room");
    top.append(roomName, element("span", "bryght-message-hit__date", formatSearchDate(hit.ts)));

    const line = element("span", "bryght-message-hit__snippet");
    const sender = element("span", "bryght-message-hit__sender");
    line.appendChild(sender);
    if (MEDIA_PREFIXES[hit.msgtype]) line.appendChild(document.createTextNode(MEDIA_PREFIXES[hit.msgtype]));

    const snippet = buildSnippet(hit.text, tokens);
    if (snippet.leading) line.appendChild(document.createTextNode("…"));
    appendHighlighted(line, snippet.text, snippet.ranges);
    if (snippet.trailing) line.appendChild(document.createTextNode("…"));

    body.append(top, line);
    button.append(this.ui.buildRoomAvatar(info, "bryght-room-avatar--small"), body);
    this.list.appendChild(button);

    const row = { hit, button, roomName, sender };
    this.rows.push(row);
    this.fillNames(row);
  }

  fillNames(row) {
    const room = this.ui.client.getRoom(row.hit.roomId);
    if (!room) return;
    const info = this.ui.getRoomDisplayInfo(room);

    if (row.roomName.textContent !== info.name) row.roomName.textContent = info.name;

    const sender = this.senderLabel(row.hit, info);
    const senderText = sender ? `${sender}: ` : "";
    if (row.sender.textContent !== senderText) row.sender.textContent = senderText;
  }

  // "Du" for your own messages, the sender's name in a group, nothing in a
  // 1:1 — the chat's name already says who wrote.
  senderLabel(hit, info) {
    if (hit.sender === this.ui.userId) return this.ui.t("search_you", "Du");
    if (info.type === "group") return this.ui.getUserInfo(hit.sender).name;
    return null;
  }

  openHit(hit) {
    const room = this.ui.client.getRoom(hit.roomId);
    if (!room) return;

    // Closes the on-screen keyboard before the conversation slides in.
    this.input.blur();
    this.rows.forEach((row) => row.button.classList.toggle("active", row.hit === hit));
    this.ui.selectRoom(room, null, { focusEventId: hit.eventId, focusTs: hit.ts, tokens: this.tokens });
  }

  render() {
    const active = this.isActive();
    this.ui.container.classList.toggle("is-searching", active);
    this.chatsTitle.hidden = !active || this.visibleChats === 0;

    const searchesMessages = this.searchesMessages();
    // A query too short for the messages still needs an answer when it
    // matches no chat either.
    this.results.hidden = !active || (!searchesMessages && this.visibleChats > 0);
    if (this.results.hidden) return;

    this.resultsTitle.hidden = !searchesMessages;
    this.moreButton.hidden = !(this.state === "done" && this.cursor);

    const noMessages = searchesMessages && this.state === "done" && this.rows.length === 0;
    this.hint.hidden = !noMessages || this.ui.searchEngine.capabilities.prefix;

    this.status.textContent = this.statusText(searchesMessages);
    // With hits on screen the count is for screen readers only.
    this.status.classList.toggle("sr-only", this.state === "done" && this.rows.length > 0);
  }

  statusText(searchesMessages) {
    if (!searchesMessages) return this.ui.t("search_no_chats", "Keine Chats gefunden");

    switch (this.state) {
      case "loading": return this.ui.t("search_loading", "Suche…");
      case "error": return this.ui.t("search_error", "Die Suche ist gerade nicht verfügbar.");
      case "done": {
        const count = this.rows.length;
        if (count === 0) return this.ui.t("search_no_messages", "Keine Nachrichten gefunden");
        if (count === 1) return this.ui.t("search_results_one", "1 Nachricht gefunden");
        return this.ui.t("search_results_other", "%{count} Nachrichten gefunden").replace("%{count}", count);
      }
      default: return "";
    }
  }
}

function element(tag, className, text = null) {
  const el = document.createElement(tag);
  el.className = className;
  if (text !== null) el.textContent = text;
  return el;
}
